import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Loader from "../components/Loader"
import RemoteImage from "../components/RemoteImage"
import SyncError from "../components/SyncError"
import ItemDetail from "../components/ItemDetail"
import { AsOfNote } from "../components/StaleBanner"
import { useMenuData } from "../offline/menuData"
import { SYNC_PHASE } from "../offline/syncEngine"
import useCloseRequest from "../hooks/useCloseRequest"
import { ACTIVITY_TYPE } from "../network/activity"
import { allergenSearch, isFilterAvailable, textSearch } from "../models/snapshot"
import { firstSyncMessage } from "../models/syncStatus"
import {
  ALLERGEN_GROUPS,
  ALL_PERIODS,
  mealPeriodOf,
  mealPeriodsFor,
  normalizeSearchResult
} from "../models/search"
import { NA } from "../models/menu"
import {
  filterModeLabels,
  isAllergenMatch,
  isDietSelection,
  orderByMatch,
  splitAllergens,
  toggleFilter
} from "../models/allergens"
import "./SearchPage.css"

const MODE = { BROWSE: "browse", TEXT: "text", ALLERGEN: "allergen" }

// Result cards that fit on an iPad before any scrolling; their images load eagerly.
const ABOVE_FOLD_CARDS = 5

export default function SearchPage({ onClose }) {
  const { menu, sync, queueActivity } = useMenuData()
  const [query, setQuery] = useState("")
  const [chosenAllergens, setChosenAllergens] = useState([])
  const [mode, setMode] = useState(MODE.BROWSE)
  const [term, setTerm] = useState("")
  const [allergens, setAllergens] = useState([])
  const [withAllergens, setWithAllergens] = useState(true)
  const [period, setPeriod] = useState(ALL_PERIODS)
  const [selectedItem, setSelectedItem] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 300)
    return () => clearTimeout(timer)
  }, [])

  // Diet chips appear once the saved menu carries their groups; an older server sends none.
  const filterGroups = useMemo(
    () =>
      ALLERGEN_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => isFilterAvailable(menu, item.apiName))
      })).filter((group) => group.items.length > 0),
    [menu]
  )
  const filterItems = useMemo(() => filterGroups.flatMap((group) => group.items), [filterGroups])
  const selectedAllergens = useMemo(
    () => chosenAllergens.filter((id) => filterItems.some((item) => item.id === id)),
    [chosenAllergens, filterItems]
  )

  const runTextSearch = useCallback(() => {
    const trimmed = query.trim()
    if (!trimmed) return
    setMode(MODE.TEXT)
    setTerm(trimmed)
    setPeriod(ALL_PERIODS)
  }, [query])

  const runAllergenSearch = useCallback(() => {
    if (!selectedAllergens.length) return
    const names = filterItems
      .filter((item) => selectedAllergens.includes(item.id))
      .map((item) => item.apiName)
    setMode(MODE.ALLERGEN)
    setAllergens(names)
    setWithAllergens(true)
    setPeriod(ALL_PERIODS)
    queueActivity(ACTIVITY_TYPE.SEARCH_ALLERGEN)
  }, [selectedAllergens, filterItems, queueActivity])

  const backToBrowse = useCallback(() => {
    setMode(MODE.BROWSE)
    setTerm("")
    setAllergens([])
  }, [])

  // Back leaves the results for the search screen; from there it pops back to the menu.
  useCloseRequest(mode !== MODE.BROWSE, backToBrowse)

  // A refreshed copy without the searched diet cannot answer it, so the chips come back.
  const isAllergenSearchUnanswerable =
    mode === MODE.ALLERGEN && allergens.some((group) => !isFilterAvailable(menu, group))
  useEffect(() => {
    if (isAllergenSearchUnanswerable) backToBrowse()
  }, [isAllergenSearchUnanswerable, backToBrowse])

  const modeLabels = filterModeLabels(allergens)

  // Both searches run against the menu saved on the device, online or not.
  const results = useMemo(() => {
    if (mode === MODE.BROWSE || !menu) return []
    if (mode === MODE.TEXT) return textSearch(menu, term).map(normalizeSearchResult)
    const { including, excluding } = allergenSearch(menu, allergens)
    return (withAllergens ? including : excluding).map(normalizeSearchResult)
  }, [mode, menu, term, allergens, withAllergens])

  const hasFailedFirstSync = !menu && sync.phase === SYNC_PHASE.IDLE && Boolean(sync.lastError)
  const isSearching = mode !== MODE.BROWSE
  const isLoading = isSearching && !menu && !hasFailedFirstSync
  const error =
    isSearching && hasFailedFirstSync ? { message: firstSyncMessage(sync.lastError) } : null

  const periods = useMemo(() => mealPeriodsFor(results), [results])
  const visible = useMemo(
    () =>
      period === ALL_PERIODS
        ? results
        : results.filter((item) => mealPeriodOf(item.categoryTag) === period),
    [results, period]
  )
  const count = visible.length

  const emptyMessage =
    mode === MODE.TEXT ? `No matches for "${term}"` : "No items match this filter"

  return (
    <div className="search">
      <header className="search__bar">
        <button
          type="button"
          className="search__back"
          aria-label="Back"
          onClick={mode === MODE.BROWSE ? onClose : backToBrowse}
        >
          <BackIcon />
        </button>

        <div className="search__field">
          <input
            ref={inputRef}
            className="search__input t-p14"
            type="search"
            value={query}
            placeholder="Search"
            enterKeyHint="search"
            autoCapitalize="none"
            autoCorrect="off"
            onChange={(event) => {
              setQuery(event.target.value)
              if (!event.target.value) backToBrowse()
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                runTextSearch()
              }
            }}
          />
        </div>

        {query ? (
          <button
            type="button"
            className="search__clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery("")
              backToBrowse()
            }}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="m6 6 12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : selectedAllergens.length > 0 && mode === MODE.BROWSE ? (
          <button type="button" className="pill pill--selected t-p14" onClick={runAllergenSearch}>
            Next
          </button>
        ) : (
          <span className="search__spacer" aria-hidden="true" />
        )}
      </header>

      <div className="search__rule" />

      <div className="search__body scroll-y">
        {mode === MODE.BROWSE ? (
          <div className="search__browse">
            <p className="search__heading t-p14">Top Allergens</p>
            {filterGroups.map((group) => (
              <section className="search__group" key={group.title}>
                <h2 className="search__group-title">{group.title}</h2>
                <div className="search__chips">
                  {group.items.map((item) => {
                    const isSelected = selectedAllergens.includes(item.id)
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`pill t-p14${isSelected ? " pill--selected" : ""}`}
                        aria-pressed={isSelected}
                        onClick={() => setChosenAllergens(toggleFilter(selectedAllergens, item.id))}
                      >
                        {item.title}
                      </button>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="search__results">
            <h2 className="search__group-title search__group-title--wide">
              FILTER BY CATEGORIES
            </h2>
            <div className="search__periods scroll-x">
              <div className="search__periods-track">
                {periods.map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    className={`pill pill--period t-sb14${entry === period ? " pill--selected" : ""}`}
                    onClick={() => setPeriod(entry)}
                  >
                    {entry}
                  </button>
                ))}
              </div>
            </div>

            {mode === MODE.ALLERGEN && (
              <div className="search__toggle">
                <ToggleRadio
                  label={modeLabels.including}
                  isSelected={withAllergens}
                  onSelect={() => setWithAllergens(true)}
                />
                <ToggleRadio
                  label={modeLabels.excluding}
                  isSelected={!withAllergens}
                  onSelect={() => setWithAllergens(false)}
                />
              </div>
            )}

            {mode === MODE.ALLERGEN && <AsOfNote className="search__as-of" />}

            {!isLoading && count > 0 && (
              <p className="search__group-title search__group-title--wide">
                {count} {count === 1 ? "RESULT FOUND" : "RESULTS FOUND"}
              </p>
            )}

            {isLoading && <Loader label="Searching" />}

            {!isLoading && error && <SyncError message={error.message} />}

            {!isLoading && !error && !visible.length && (
              <p className="search__empty t-y24">{emptyMessage}</p>
            )}

            {!isLoading && !error && visible.length > 0 && (
              <ul className="search__cards">
                {visible.map((item, index) => (
                  <li key={`${item.id}-${index}`}>
                    <ResultCard
                      item={item}
                      term={mode === MODE.TEXT ? term : ""}
                      selectedAllergens={mode === MODE.ALLERGEN ? allergens : []}
                      withAllergens={withAllergens}
                      isAboveFold={index < ABOVE_FOLD_CARDS}
                      onSelect={() => setSelectedItem(item.detail)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {selectedItem && <ItemDetail item={selectedItem} onClose={() => setSelectedItem(null)} />}
    </div>
  )
}

function ToggleRadio({ label, isSelected, onSelect }) {
  return (
    <button
      type="button"
      className="search__radio"
      role="radio"
      aria-checked={isSelected}
      onClick={onSelect}
    >
      <span className={`search__radio-dot${isSelected ? " search__radio-dot--on" : ""}`} />
      <span className="t-p14">{label}</span>
    </button>
  )
}

function ResultCard({ item, term, selectedAllergens, withAllergens, isAboveFold, onSelect }) {
  const isFiltering = selectedAllergens.length > 0

  return (
    <button
      type="button"
      className={`result${isFiltering ? " result--framed" : ""}`}
      data-allergen-mode={isFiltering ? (withAllergens ? "with" : "without") : undefined}
      data-filter-kind={isFiltering && isDietSelection(selectedAllergens) ? "diet" : undefined}
      onClick={onSelect}
    >
      <RemoteImage
        className="result__image"
        src={item.thumbnailUrl || item.imageUrl}
        fallbackSrc={item.imageUrl}
        alt=""
        isPriority={isAboveFold}
      />
      <div className="result__body">
        {item.categoryTag && <span className="result__tag">{item.categoryTag}</span>}
        <div className="result__heading">
          {/* A translatable field can come back null; never leave the card untitled. */}
          <span className="result__name t-sb18">{item.name || NA}</span>
          {item.price && <span className="result__price t-p14">{item.price}</span>}
        </div>
        {item.description && (
          <p className="result__description t-p12">
            <Highlighted text={item.description} term={term} />
          </p>
        )}
        {item.lines.map((line) => (
          <p className="result__line t-p14" key={line.label}>
            <span className="result__label">{line.label}</span>
            {line.key === "allergens" && isFiltering ? (
              <AllergenValue value={line.value} selectedAllergens={selectedAllergens} />
            ) : (
              <span className="result__value">
                <Highlighted text={line.value} term={term} />
              </span>
            )}
          </p>
        ))}
        <p className="result__line t-p14">
          <span className="result__label">Chef Description: </span>
          <span className="result__more">Click here...</span>
        </p>
      </div>
    </button>
  )
}

// Filtered allergens lead the line in red, as in the native cell; a lone diet's labels in gold.
function AllergenValue({ value, selectedAllergens }) {
  if (!value || value === NA) return <span className="result__value">{value || NA}</span>
  const hitClass = isDietSelection(selectedAllergens) ? "result__diet-hit" : "result__allergen-hit"

  return orderByMatch(splitAllergens(value), selectedAllergens).map((part, index) => (
    <span key={`${part}-${index}`}>
      {index > 0 && <span className="result__value">, </span>}
      <span
        className={isAllergenMatch(part, selectedAllergens) ? hitClass : "result__value"}
      >
        {part}
      </span>
    </span>
  ))
}

// Bolds the first occurrence of the search term in brand gold, as the native card does.
function Highlighted({ text, term }) {
  if (!term) return text
  const index = text.toLowerCase().indexOf(term.toLowerCase())
  if (index < 0) return text
  return (
    <>
      {text.slice(0, index)}
      <span className="result__match">{text.slice(index, index + term.length)}</span>
      {text.slice(index + term.length)}
    </>
  )
}

// Back arrow with a shaft, matching the native menu search header.
function BackIcon() {
  return (
    <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M15.5 9H2.5M8.5 3 2.5 9l6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
      />
    </svg>
  )
}
